// src/pages/ProductsListPage.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "../config/api";

/** ---------- Image resize helpers (same behavior as ProductsAddPage) ---------- */
type ResizeOpts = {
  maxWidth?: number;
  maxHeight?: number;
  mimeType?: "image/jpeg" | "image/webp" | "image/png";
  quality?: number;        // 0..1 (jpeg/webp เท่านั้น)
  targetMaxBytes?: number; // เช่น 1.5MB
};

async function fileToImage(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  await new Promise<void>((resolve, reject) => {
    const probe = new Image();
    probe.onload = () => resolve();
    probe.onerror = reject;
    probe.src = url;
  });
  const img = new Image();
  img.src = url;
  return img;
}

async function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("toBlob_failed"))), type, quality);
  });
}

/** ย่อรูป + ลดคุณภาพจนต่ำกว่า targetMaxBytes (ถ้ากำหนด) โดยไม่ขยายรูป */
async function resizeImage(
  file: File,
  opts: ResizeOpts = {}
): Promise<{ blob: Blob; file: File; previewUrl: string }> {
  const {
    maxWidth = 1280,
    maxHeight = 1280,
    mimeType = "image/jpeg",
    quality = 0.85,
    targetMaxBytes,
  } = opts;

  // heic/heif -> jpeg เพื่อความเข้ากันได้
  const outType = /heic|heif/i.test(file.type) ? "image/jpeg" : mimeType;

  const img = await fileToImage(file);

  let { width, height } = img;
  const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
  width = Math.round(width * ratio);
  height = Math.round(height * ratio);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0, width, height);

  let q = quality;
  let blob = await canvasToBlob(canvas, outType, q);

  if (targetMaxBytes && blob.size > targetMaxBytes) {
    while (q > 0.5 && blob.size > targetMaxBytes) {
      q -= 0.07;
      blob = await canvasToBlob(canvas, outType, q);
    }
  }

  const newName =
    file.name.replace(/\.(heic|heif|webp|png|jpg|jpeg)$/i, "") + (outType === "image/webp" ? ".webp" : ".jpg");
  const resizedFile = new File([blob], newName, { type: outType });
  const previewUrl = URL.createObjectURL(blob);

  return { blob, file: resizedFile, previewUrl };
}
/** --------------------------------------------------------------------- */

type Product = {
  id: number;
  code: string;
  name: string;
  price: number;
  wholesalePrice: number;
  mallPrice: number;
  stockOnHand: number;
  unit?: string;
  createdAt?: string;
  imageUrl?: string; // SAS 1 ชม. จาก backend
  thumbUrl?: string; 
};

type Movement = {
  id: number;
  product_code: string;
  qty: number;
  qty_sell: number;
  unit_cost: number;
  reason: "PURCHASE" | "SALE" | "ADJUST";
  ref_table: string | null;
  ref_no: string;
  created_at: string;
  status: string | null;
};

type Alert = { show: boolean; message: string; type?: "success" | "warning" | "info" | "danger" };

export default function ProductsListPage(): JSX.Element {
  /** ---------- UI State ---------- */
  const [items, setItems] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [toast, setToast] = useState<Alert>({ show: false, message: "", type: "success" });

  // search
  const [q, setQ] = useState<string>("");
  const [qInput, setQInput] = useState<string>("");

  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(50);   // หรือ 30 แล้วแต่ใจเรา
  const [total, setTotal] = useState<number>(0);

  // image preview (hover)
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // ----- Upload Modal states -----
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadCode, setUploadCode] = useState<string>(""); // รหัสสินค้าที่จะอัป
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>("");
  const [submittingUpload, setSubmittingUpload] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{ type: "success" | "warning" | "danger" | "" | "info"; msg: string }>({
    type: "",
    msg: "",
  });
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);

  // ----- Movement Modal states -----
  const [showMovModal, setShowMovModal] = useState(false);
  const [movLoading, setMovLoading] = useState(false);
  const [movError, setMovError] = useState<string>("");
  const [movItems, setMovItems] = useState<Movement[]>([]);
  const [movCode, setMovCode] = useState<string>("");
  const [movLimit] = useState<number>(50);
  const [movOffset, setMovOffset] = useState<number>(0);

  // ----- Stock Location Popover -----
  type StockByLoc = Record<string, number>;
  const STOCK_LOCATIONS = ["MAIN", "SHOP", "TIKTOK", "USA"];
  const LOCATION_BADGE: Record<string, string> = {
    MAIN: "bg-primary", SHOP: "bg-success", TIKTOK: "bg-warning text-dark", USA: "bg-danger",
  };
  const [stockPopoverCode, setStockPopoverCode] = useState<string | null>(null);
  const [stockPopoverPos, setStockPopoverPos] = useState<{ top: number; left: number; above: boolean }>({ top: 0, left: 0, above: true });
  const [stockPinned, setStockPinned] = useState<string | null>(null);
  const stockCache = useRef<Map<string, StockByLoc>>(new Map());
  const [stockLoadingSet, setStockLoadingSet] = useState<Set<string>>(new Set());
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // PMN-CRITICAL(/ProductsListPage): Popover totals are computed from available lots by location and may differ from products.stock_on_hand.
  const fetchStockByLoc = useCallback(async (code: string) => {
    if (stockCache.current.has(code)) return;
    setStockLoadingSet((prev) => new Set(prev).add(code));
    try {
      const resp = await fetch(apiUrl(`/stock-by-location/${encodeURIComponent(code)}/lots`));
      const data = await resp.json().catch(() => ({}));
      const lots: any[] = Array.isArray(data.items) ? data.items : [];
      const byLoc: StockByLoc = { MAIN: 0, SHOP: 0, TIKTOK: 0, USA: 0 };
      for (const lot of lots) {
        const loc = String(lot.location_code ?? "").toUpperCase();
        byLoc[loc] = (byLoc[loc] ?? 0) + Number(lot.available_qty ?? 0);
      }
      stockCache.current.set(code, byLoc);
    } catch {}
    setStockLoadingSet((prev) => { const s = new Set(prev); s.delete(code); return s; });
  }, []);

  const calcPos = (el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    const popH = 180;
    const above = rect.top > popH + 12;
    const top = above ? rect.top - popH - 8 : rect.bottom + 8;
    const left = Math.min(rect.right - 188, window.innerWidth - 200);
    return { top, left, above };
  };

  const handleStockMouseEnter = useCallback((code: string, el: HTMLElement) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      setStockPopoverPos(calcPos(el));
      setStockPopoverCode(code);
      fetchStockByLoc(code);
    }, 180);
  }, [fetchStockByLoc]);

  const handleStockMouseLeave = useCallback(() => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      setStockPopoverCode((cur) => (stockPinned ? cur : null));
    }, 200);
  }, [stockPinned]);

  const handleStockClick = useCallback((code: string, el: HTMLElement) => {
    fetchStockByLoc(code);
    setStockPopoverPos(calcPos(el));
    setStockPopoverCode(code);
    setStockPinned((prev) => (prev === code ? null : code));
  }, [fetchStockByLoc]);

  // ปิด popover เมื่อคลิกนอก
  useEffect(() => {
    if (!stockPinned) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-stock-popover]")) {
        setStockPinned(null);
        setStockPopoverCode(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [stockPinned]);

  // อัปได้เมื่อมี code + file + ไม่ติดกำลังอัปโหลด
  const canUpload = useMemo(() => !!uploadCode.trim() && !!file && !submittingUpload, [uploadCode, file, submittingUpload]);

  /** ---------- Toast auto-hide ---------- */
  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast({ show: false, message: "" }), 2200);
    return () => clearTimeout(t);
  }, [toast.show]);

  function extractCodeNumber(code: string): number | null {
    const m = String(code).match(/(\d+)(?!.*\d)/); // เลขชุดสุดท้ายในสตริง
    return m ? Number(m[1]) : null;
  }

  /** ---------- fetch products ---------- */
  // PMN-CRITICAL(/ProductsListPage): /productsListNew aliases, stockOnHand, and SAS image URLs drive this table; keep response shape stable.
  const fetchProducts = async (query?: string, pageParam?: number) => {
    try {
      setLoading(true);
      setErrorMsg("");
  
      const pageToUse = pageParam ?? page;
      const qs: string[] = [];
  
      if (query && query.trim()) {
        qs.push(`q=${encodeURIComponent(query.trim())}`);
      }
  
      qs.push(`page=${pageToUse}`);
      qs.push(`limit=${limit}`);
  
      const url = apiUrl(`/productsListNew${qs.length ? "?" + qs.join("&") : ""}`);
  
      const resp = await fetch(url);
      const json = await resp.json();
      if (!resp.ok) throw new Error(json?.error ?? resp.statusText);
  
      const list: Product[] = (json.items ?? []).map((r: any) => ({
        id: r.id,
        code: r.code,
        name: r.name,
        price: Number(r.price ?? 0),
        wholesalePrice: Number(r.wholesalePrice ?? 0),
        mallPrice: Number(r.mallPrice ?? 0),
        stockOnHand: Number(r.stockOnHand ?? 0),
        unit: r.unit ?? undefined,
        createdAt: r.createdAt ?? undefined,
        imageUrl: r.imageUrl ?? undefined,
        thumbUrl: r.thumbUrl ?? r.imageUrl ?? undefined,
      }));
  
      // 👇 เพิ่มส่วนนี้ให้เรียงตามเลขท้ายโค้ด
      const sorted = [...list].sort((a, b) => {
        const na = extractCodeNumber(a.code);
        const nb = extractCodeNumber(b.code);
  
        if (na != null && nb != null && na !== nb) return na - nb;
        if (na != null && nb == null) return -1;
        if (na == null && nb != null) return 1;
  
        // fallback ถ้าไม่มีเลขท้าย หรือเลขเท่ากัน
        return a.code.localeCompare(b.code, "th-TH", { numeric: true, sensitivity: "base" });
      });
  
      setItems(sorted);
      setTotal(Number(json.total ?? 0));
      setPage(Number(json.page ?? pageToUse));
    } catch (err: any) {
      const msg = err?.message ?? "โหลดรายการสินค้าไม่สำเร็จ";
      setErrorMsg(msg);
      setToast({ show: true, message: msg, type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  
  useEffect(() => {
    fetchProducts(undefined, 1); // หน้าแรก
  }, []);

  // ค้นหาแบบ server
  const doSearch = () => {
    const qVal = qInput.trim();
    setQ(qVal);
    setPage(1);
    fetchProducts(qVal || undefined, 1);
  };

  // รีเฟรช (ดึง SAS ใหม่แก้เคสหมดอายุ)
  const refresh = () => {
    fetchProducts(q || undefined, page);
  };

  // กรองฝั่ง client
  const filtered = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      const na = extractCodeNumber(a.code);
      const nb = extractCodeNumber(b.code);
    
      // ตัวเลขมาก → น้อย
      if (na !== null && nb !== null && na !== nb) return nb - na;
    
      // ถ้าเลขเท่ากัน หรือไม่ใช่ตัวเลข → เรียงโค้ดแบบตัวอักษรจาก Z → A
      return b.code.localeCompare(a.code, undefined, { numeric: true, sensitivity: "base" });
    });
    return copy;
  }, [items]);

  /** ---------- Upload handlers ---------- */
  const openUploadModal = (code: string) => {
    setUploadCode(code);
    setFile(null);
    setPreview("");
    setUploadStatus({ type: "", msg: "" });
    if (galleryInputRef.current) galleryInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    setShowUploadModal(true);
  };
  const closeUploadModal = () => setShowUploadModal(false);

  const openGalleryPicker = () => {
    if (!galleryInputRef.current) return;
    galleryInputRef.current.value = "";
    galleryInputRef.current.click();
  };
  const openCameraPicker = () => {
    if (!cameraInputRef.current) return;
    cameraInputRef.current.value = "";
    cameraInputRef.current.click();
  };
  const clearUploadFile = () => {
    setFile(null);
    setPreview("");
    setUploadStatus({ type: "", msg: "" });
    if (galleryInputRef.current) galleryInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  // เลือกไฟล์ + ย่อ/บีบอัด
  const onPick = async (f: File | null) => {
    setUploadStatus({ type: "", msg: "" });

    if (!f) {
      setFile(null);
      setPreview("");
      return;
    }

    try {
      const { file: resized, previewUrl } = await resizeImage(f, {
        maxWidth: 1280,
        maxHeight: 1280,
        mimeType: "image/jpeg",
        quality: 0.85,
        targetMaxBytes: 1.5 * 1024 * 1024, // ~1.5MB
      });
      setFile(resized);
      setPreview(previewUrl);
    } catch {
      // ถ้าย่อไม่สำเร็จ ใช้ไฟล์เดิม
      setFile(f);
      setPreview(URL.createObjectURL(f));
      setUploadStatus({ type: "warning", msg: "ย่อรูปไม่สำเร็จ ใช้ไฟล์เดิมแทน" });
    }
  };

  const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0] ?? null;
    void onPick(f);
  };

  // PMN-CRITICAL(/ProductsListPage): Field name must stay "file"; /upload/:code writes Azure blobs and updates products.url_images/url_images_thumb.
  const uploadImage = async () => {
    if (!canUpload) return;
    try {
      setSubmittingUpload(true);
      setUploadStatus({ type: "info" as any, msg: "กำลังอัปโหลด..." });
  
      const code = uploadCode.trim();
      const fd = new FormData();
      fd.append("file", file!);
  
      const endpoint = apiUrl(`/upload/${encodeURIComponent(code)}`);
      const res = await fetch(endpoint, { method: "POST", body: fd });
  
      const ct = res.headers.get("content-type") || "";
      const payload: any = ct.includes("application/json") ? await res.json() : await res.text();
  
      // 👇 เพิ่ม log ชั่วคราว
      console.log("UPLOAD RESPONSE", res.status, payload);
  
      if (!res.ok || (typeof payload === "object" && payload?.ok === false)) {
        const msg = (typeof payload === "object" ? payload?.error : String(payload)) || "upload_failed";
        throw new Error(msg);
      }
  
      const url = (typeof payload === "object" ? payload?.url : undefined) as string | undefined;
      setUploadStatus({ type: "success", msg: `อัปโหลดสำเร็จ! ${url ? `URL: ${url}` : ""}` });
  
      await fetchProducts(q || undefined);   // ✅ ตรงนี้ใช้ของใหม่ได้
      setToast({ show: true, message: "อัปโหลดรูปสำเร็จ", type: "success" });
      setShowUploadModal(false);
    } catch (err: any) {
      const emsg = String(err?.message || "upload_failed");
      const friendly = emsg === "product_not_found" ? "ไม่พบรหัสสินค้านี้ในระบบ กรุณาตรวจสอบอีกครั้ง" : emsg;
      setUploadStatus({ type: "danger", msg: `ล้มเหลว: ${friendly}` });
    } finally {
      setSubmittingUpload(false);
    }
  };
  

  /** ---------- Movement handlers ---------- */
  const openMovements = async (code: string) => {
    setMovCode(code);
    setMovItems([]);
    setMovError("");
    setMovOffset(0);
    setShowMovModal(true);
    await fetchMovements(code, 0);
  };

  // PMN-NOTE(/ProductsListPage): Movement modal shows raw inventory_movements rows, not a reconciled stock summary.
  const fetchMovements = async (code: string, offset: number) => {
    try {
      setMovLoading(true);
      setMovError("");

      const url = apiUrl(
        `/inventory_movements?product_code=${encodeURIComponent(code)}&limit=${movLimit}&offset=${offset}`
      );

      const resp = await fetch(url);
      const ct = resp.headers.get("content-type") || "";
      const isJson = ct.includes("application/json");
      const payload = isJson ? await resp.json() : await resp.text();

      if (!resp.ok) {
        const msg = isJson ? (payload?.error ?? resp.statusText) : `HTTP ${resp.status} — ${String(payload).slice(0, 120)}`;
        throw new Error(msg);
      }

      const list: Movement[] = (payload.items ?? []).map((r: any) => ({
        id: r.id,
        product_code: r.product_code,
        qty: Number(r.qty ?? 0),
        qty_sell: Number(r.qty_sell ?? 0),
        unit_cost: Number(r.unit_cost ?? 0),
        reason: r.reason,
        ref_table: r.ref_table ?? null,
        ref_no: r.ref_no ?? "-",
        created_at: r.created_at,
        status: r.status ?? null,
      }));
      setMovItems(list);
    } catch (err: any) {
      setMovError(err?.message ?? "โหลดประวัติไม่สำเร็จ");
    } finally {
      setMovLoading(false);
    }
  };

  const nextMovPage = () => {
    const newOffset = movOffset + movLimit;
    setMovOffset(newOffset);
    fetchMovements(movCode, newOffset);
  };
  const prevMovPage = () => {
    const newOffset = Math.max(movOffset - movLimit, 0);
    setMovOffset(newOffset);
    fetchMovements(movCode, newOffset);
  };

  /** ---------- UI ---------- */
  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      {/* Toast มุมขวาบน */}
      <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
        {toast.show && (
          <div className={`alert alert-${toast.type ?? "success"} shadow mb-0 rounded-3 py-2 px-3`}>{toast.message}</div>
        )}
      </div>

      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-9">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4 p-md-5">
                {/* Header + actions */}
                <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                  <h4 className="mb-0 text-primary">รายการสินค้า  (Products)</h4>
                  <div className="d-flex flex-wrap gap-2">
                    <Link to="/ProductsAddPage" className="btn btn-success btn-sm d-inline-flex align-items-center gap-2">
                      <span className="fw-bold">+</span> เพิ่มสินค้าใหม่
                    </Link>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={refresh}>
                      รีเฟรช
                    </button>
                    <a
                      href={apiUrl("/products/export-excel") + (q ? `?q=${encodeURIComponent(q)}` : "")}
                      className="btn btn-outline-success btn-sm"
                      download
                    >
                      Export Excel
                    </a>
                  </div>
                </div>

                {/* ค้นหา */}
                <div className="input-group mb-3">
                  <input
                    className="form-control"
                    placeholder="ค้นหาโค้ด/ชื่อ/หน่วย (กด Enter เพื่อค้นหา server)"
                    value={qInput}
                    onChange={(e) => setQInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && doSearch()}
                  />
                  <button className="btn btn-primary" type="button" onClick={doSearch}>
                    ค้นหา
                  </button>
                </div>

                {/* สถานะโหลด/ผิดพลาด */}
                {loading && (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <span>กำลังโหลดรายการสินค้า…</span>
                  </div>
                )}
                {errorMsg && (
                  <div className="alert alert-warning rounded-3" role="alert">
                    โหลดสินค้าไม่สำเร็จ: {errorMsg}
                  </div>
                )}

                {/* ตารางสินค้า */}
                <div className="border rounded-3 p-0 bg-white">
                  <div className="table-responsive" style={{ maxHeight: 520 }}>
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th style={{ width: 76 }}>รูป</th>
                          <th style={{ width: 160 }}>โค้ด</th>
                          <th>ชื่อสินค้า</th>
                          <th style={{ width: 140 }} className="text-end">ราคาปลีก</th>
                          <th style={{ width: 140 }} className="text-end">ราคาห้าง</th>
                          <th style={{ width: 140 }} className="text-end">ราคาส่ง</th>
                          <th style={{ width: 120 }} className="text-end">คงเหลือ</th>
                          <th style={{ width: 240 }} className="text-end">จัดการ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.length === 0 && !loading && (
                          <tr>
                            <td colSpan={8} className="text-center text-muted py-4">
                              ไม่พบรายการ
                            </td>
                          </tr>
                        )}

                        {filtered.map((p) => (
                          <tr key={p.id} className="user-select-none">
                            <td>
                              {(() => {
                                const thumbSrc = p.thumbUrl || p.imageUrl || null;
                                const fullSrc = p.imageUrl || thumbSrc || null;

                                return thumbSrc ? (
                                  <img
                                    src={thumbSrc}               // ✅ ใช้รูปเล็กเวลาอยู่ในตาราง
                                    alt={p.name}
                                    width={56}
                                    height={56}
                                    loading="lazy"
                                    decoding="async"
                                    style={{ objectFit: "cover", borderRadius: 8, background: "#f5f5f5" }}
                                    onMouseEnter={() => setPreviewImage(fullSrc)}   // ✅ hover เป็นรูปใหญ่
                                    onMouseLeave={() => setPreviewImage(null)}
                                    onError={(e) => {
                                      (e.currentTarget as HTMLImageElement).style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <div className="bg-light border rounded" style={{ width: 56, height: 56 }} />
                                );
                              })()}
                            </td>
                            <td className="fw-semibold">{p.code}</td>
                            <td>{p.name}</td>
                            <td className="text-end">
                              {p.price.toLocaleString("th-TH", { style: "currency", currency: "THB" })}
                            </td>
                            <td className="text-end">
                              {p.mallPrice.toLocaleString("th-TH", { style: "currency", currency: "THB" })}
                            </td>
                            <td className="text-end">
                              {p.wholesalePrice.toLocaleString("th-TH", { style: "currency", currency: "THB" })}
                            </td>
                            <td
                              className="text-end"
                              data-stock-popover
                              onMouseEnter={(e) => handleStockMouseEnter(p.code, e.currentTarget)}
                              onMouseLeave={handleStockMouseLeave}
                            >
                              <span
                                style={{
                                  cursor: "pointer",
                                  textDecoration: "underline dotted",
                                  textUnderlineOffset: 3,
                                }}
                                onClick={(e) => handleStockClick(p.code, e.currentTarget.parentElement as HTMLElement)}
                              >
                                {p.stockOnHand}
                              </span>

                              {stockPopoverCode === p.code && (
                                <div
                                  data-stock-popover
                                  style={{
                                    position: "fixed",
                                    top: stockPopoverPos.top,
                                    left: stockPopoverPos.left,
                                    zIndex: 1050,
                                    width: 188,
                                    background: "#fff",
                                    border: "1px solid #dee2e6",
                                    borderRadius: 8,
                                    boxShadow: "0 4px 16px rgba(0,0,0,0.13)",
                                    padding: "10px 14px",
                                    textAlign: "left",
                                  }}
                                >
                                  <div className="fw-semibold small mb-2" style={{ whiteSpace: "nowrap" }}>
                                    Stock จริง (inventory)
                                    {stockPinned === p.code && (
                                      <span className="ms-2 text-muted" style={{ fontSize: 10 }}>📌 ปักหมุด</span>
                                    )}
                                  </div>
                                  {stockLoadingSet.has(p.code) ? (
                                    <div className="text-muted small d-flex align-items-center gap-1">
                                      <div className="spinner-border spinner-border-sm" />
                                      <span>กำลังโหลด…</span>
                                    </div>
                                  ) : (() => {
                                    const byLoc = stockCache.current.get(p.code) ?? {};
                                    const total = STOCK_LOCATIONS.reduce((s, loc) => s + (byLoc[loc] ?? 0), 0);
                                    return (
                                      <>
                                        {STOCK_LOCATIONS.map((loc) => {
                                          const qty = byLoc[loc] ?? 0;
                                          return (
                                            <div key={loc} className="d-flex justify-content-between align-items-center gap-3 mb-1">
                                              <span className={`badge ${LOCATION_BADGE[loc] ?? "bg-secondary"}`} style={{ fontSize: 11 }}>
                                                {loc}
                                              </span>
                                              <span className={`small fw-semibold ${qty === 0 ? "text-muted" : ""}`}>
                                                {qty}
                                              </span>
                                            </div>
                                          );
                                        })}
                                        <hr className="my-1" />
                                        <div className="d-flex justify-content-between align-items-center">
                                          <span className="small text-muted">รวม</span>
                                          <span className="small fw-bold">{total}</span>
                                        </div>
                                        {total !== p.stockOnHand && (
                                          <div className="mt-1 text-warning small" style={{ fontSize: 10 }}>
                                            ⚠️ ต่างจาก stock_on_hand ({p.stockOnHand})
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                            </td>
                            <td className="text-end">
                              <div className="btn-group btn-group-sm">
                                <button
                                  type="button"
                                  className="btn btn-outline-primary"
                                  title="อัปโหลดรูป"
                                  onClick={() => openUploadModal(p.code)}
                                >
                                  อัปโหลดรูป
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  title="ประวัติการเคลื่อนไหว"
                                  onClick={() => openMovements(p.code)}
                                >
                                  ประวัติ
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="small text-muted">
                    แสดง {items.length} รายการ / ทั้งหมด {total} รายการ
                  </div>
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={page <= 1 || loading}
                      onClick={() => {
                        const newPage = page - 1;
                        setPage(newPage);
                        fetchProducts(q || undefined, newPage);
                      }}
                    >
                      ← ก่อนหน้า
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={page * limit >= total || loading}
                      onClick={() => {
                        const newPage = page + 1;
                        setPage(newPage);
                        fetchProducts(q || undefined, newPage);
                      }}
                    >
                      ถัดไป →
                    </button>
                  </div>
                </div>

                <div className="text-center small text-muted mt-3">
                  * ใช้สไตล์การ์ด/ปุ่ม/พื้นหลังเดียวกับหน้าเพิ่มสินค้าเพื่อความสอดคล้อง
                </div>
                <a href="/" className="btn btn-link text-decoration-none flex-fill text-center">
                  ← กลับหน้าเมนู
                </a>
              </div>
            </div>

            {/* Hover Preview กลางจอ */}
            {previewImage && (
              <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ backgroundColor: "rgba(0,0,0,0.7)", zIndex: 2000, pointerEvents: "none" }}
              >
                <img
                  src={previewImage}
                  alt="preview"
                  style={{
                    maxWidth: "90%",
                    maxHeight: "90%",
                    borderRadius: 12,
                    boxShadow: "0 0 20px rgba(0,0,0,0.5)",
                    background: "#fff",
                    transition: "opacity .15s ease",
                    opacity: 1,
                  }}
                />
              </div>
            )}

            {/* ---------- Modal อัปโหลดรูป ---------- */}
            {showUploadModal && (
              <div
                className="modal fade show"
                role="dialog"
                style={{ display: "block", backgroundColor: "rgba(0,0,0,.5)" }}
                aria-modal="true"
                aria-labelledby="uploadModalLabel"
                onClick={(e) => { if (e.target === e.currentTarget) closeUploadModal(); }}
              >
                <div className="modal-dialog modal-md">
                  <div className="modal-content rounded-4 shadow">
                    <div className="modal-header">
                      <h5 className="modal-title" id="uploadModalLabel">อัปโหลดรูปสินค้า</h5>
                      <button type="button" className="btn-close" aria-label="Close" onClick={closeUploadModal} />
                    </div>

                    <div className="modal-body">
                      <div className="mb-3">
                        <label className="form-label">รหัสสินค้า</label>
                        <input
                          className="form-control"
                          value={uploadCode}
                          onChange={(e) => setUploadCode(e.target.value.trim())}
                          placeholder="เช่น dc025"
                        />
                      </div>

                      {/* dropzone + preview */}
                      <div className="mb-2">
                        <label className="form-label d-flex justify-content-between align-items-center">
                          ไฟล์รูปภาพ
                          {file && <span className="badge text-bg-secondary">{file.name}</span>}
                        </label>

                        <div
                          onDrop={onDrop}
                          onDragOver={(e) => e.preventDefault()}
                          className="w-100 d-flex align-items-center justify-content-center bg-light"
                          style={{
                            border: "2px dashed #c9ccd1",
                            borderRadius: 12,
                            padding: 20,
                            cursor: "pointer",
                            minHeight: 120,
                          }}
                          onClick={openGalleryPicker}
                          title="ลากรูปมาวาง หรือคลิกเพื่อเลือกจากอัลบั้ม"
                        >
                          <input
                            ref={galleryInputRef}
                            type="file"
                            accept="image/*"
                            onChange={(e) => void onPick(e.target.files?.[0] ?? null)}
                            style={{ display: "none" }}
                          />
                          <input
                            ref={cameraInputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={(e) => void onPick(e.target.files?.[0] ?? null)}
                            style={{ display: "none" }}
                          />
                          {preview ? (
                            <img
                              src={preview}
                              alt="preview"
                              className="img-fluid shadow-sm"
                              style={{ maxHeight: 220, borderRadius: 10, objectFit: "contain", background: "#fff" }}
                            />
                          ) : (
                            <div className="text-muted text-center">ลากรูปมาวางที่นี่ หรือคลิกเพื่อเลือกจากอัลบั้ม</div>
                          )}
                        </div>
                        <div className="d-grid d-sm-flex gap-2 mt-2">
                          <button
                            type="button"
                            className="btn btn-outline-primary flex-fill"
                            onClick={openGalleryPicker}
                          >
                            เลือกจากอัลบั้ม
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary flex-fill"
                            onClick={openCameraPicker}
                          >
                            ถ่ายรูปใหม่
                          </button>
                        </div>
                        <div className="form-text">
                          ระบบจะย่อรูปให้อัตโนมัติ (≤ 1280px / ~1.5MB) — รองรับ jpeg, png, webp, heic/heif ฯลฯ
                        </div>
                      </div>

                      {uploadStatus.msg && (
                        <div className={`alert alert-${uploadStatus.type || "secondary"} rounded-3 mt-2`} role="alert">
                          {uploadStatus.msg}
                        </div>
                      )}
                    </div>

                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={clearUploadFile}
                      >
                        ล้างไฟล์
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!canUpload}
                        onClick={uploadImage}
                      >
                        {submittingUpload ? "กำลังอัปโหลด…" : "📤 อัปโหลด"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* ---------- /Modal อัปโหลดรูป ---------- */}

            {/* ---------- Modal: Movement History ---------- */}
            {showMovModal && (
              <div
                className="modal fade show"
                role="dialog"
                style={{ display: "block", backgroundColor: "rgba(0,0,0,.5)" }}
                aria-modal="true"
                aria-labelledby="movModalLabel"
                onClick={(e) => { if (e.target === e.currentTarget) setShowMovModal(false); }}
              >
                <div className="modal-dialog modal-lg modal-dialog-scrollable">
                  <div className="modal-content rounded-4 shadow">
                    <div className="modal-header">
                      <h5 className="modal-title" id="movModalLabel">ประวัติความเคลื่อนไหว: {movCode}</h5>
                      <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowMovModal(false)} />
                    </div>

                    <div className="modal-body">
                      {movLoading && (
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <div className="spinner-border spinner-border-sm" role="status" />
                          <span>กำลังโหลดประวัติ…</span>
                        </div>
                      )}
                      {movError && (
                        <div className="alert alert-warning rounded-3" role="alert">
                          โหลดประวัติไม่สำเร็จ: {movError}
                        </div>
                      )}

                      <div className="table-responsive" style={{ maxHeight: 420 }}>
                        <table className="table table-sm table-hover align-middle mb-0">
                          <thead className="table-light sticky-top">
                            <tr>
                              <th style={{ width: 140 }}>วันที่</th>
                              <th style={{ width: 100 }}>เหตุผล</th>
                              <th className="text-end" style={{ width: 100 }}>รับเข้า (+)</th>
                              <th className="text-end" style={{ width: 100 }}>จ่ายออก (-)</th>
                              <th className="text-end" style={{ width: 120 }}>ต้นทุน/หน่วย</th>
                              <th>อ้างอิง</th>
                            </tr>
                          </thead>
                          <tbody>
                            {movItems.length === 0 && !movLoading && (
                              <tr>
                                <td colSpan={6} className="text-center text-muted py-3">ไม่มีข้อมูล</td>
                              </tr>
                            )}
                            {movItems.map((m) => (
                                <tr
                                    key={m.id}
                                    style={m.status === "sold_out" ? { backgroundColor: "rgba(25, 135, 84, 0.12)" } : undefined}
                                    title={m.status === "sold_out" ? "ขายหมด (sold out)" : undefined}
                                >
                                    <td>{new Date(m.created_at).toLocaleString("th-TH")}</td>
                                    <td>
                                    {m.reason === "PURCHASE" ? "รับเข้า"
                                        : m.reason === "SALE" ? "ขายออก"
                                        : "ปรับสต็อก"}
                                    {m.status === "sold_out" && (
                                        <span className="ms-2 badge rounded-pill text-bg-success" style={{ opacity: 0.85 }}>
                                        sold out
                                        </span>
                                    )}
                                    </td>
                                    <td className="text-end">{m.qty > 0 ? m.qty : "-"}</td>
                                    <td className="text-end">{m.qty_sell > 0 ? m.qty_sell : "-"}</td>
                                    <td className="text-end">
                                    {m.unit_cost.toLocaleString("th-TH", { style: "currency", currency: "THB" })}
                                    </td>
                                    <td>{m.ref_table || "-"} {m.ref_no ? `#${m.ref_no}` : ""}</td>
                                </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="modal-footer d-flex justify-content-between">
                      <div className="btn-group">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          disabled={movOffset === 0 || movLoading}
                          onClick={prevMovPage}
                        >
                          ← ก่อนหน้า
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          disabled={movItems.length < movLimit || movLoading}
                          onClick={nextMovPage}
                        >
                          ถัดไป →
                        </button>
                      </div>
                      <button type="button" className="btn btn-secondary" onClick={() => setShowMovModal(false)}>
                        ปิด
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* ---------- /Modal: Movement History ---------- */}
          </div>
        </div>
      </div>
    </div>
  );
}
